import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car, CheckCircle2, FileText, Receipt, Calculator, Users, FolderOpen, MonitorSmartphone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { villes } from "@/data/villes";

const services = [
  { icon: FileText, title: "Gestion administrative", text: "Dossiers, courriers, relations avec les organismes." },
  { icon: Receipt, title: "Facturation & relances", text: "Devis, factures, suivi des règlements." },
  { icon: Calculator, title: "Pré-comptabilité", text: "Pièces classées et prêtes pour votre comptable." },
  { icon: Users, title: "Assistance RH", text: "Contrats, DPAE, suivi administratif des salariés." },
  { icon: FolderOpen, title: "Organisation documentaire", text: "Tri, classement, archivage, dématérialisation." },
  { icon: MonitorSmartphone, title: "Démarches en ligne", text: "URSSAF, impôts, CAF, ANTS, retraite… pas à pas." },
];

const Cta = ({ ville }) => (
  <div className="flex flex-wrap items-center gap-4">
    <Link
      to="/rendez-vous"
      data-testid="ville-cta-rdv"
      className="group inline-flex items-center gap-2 rounded-full bg-argia-red px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-argia-redDark hover:-translate-y-0.5"
    >
      Prendre rendez-vous
      <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
    <a href="tel:+33633837456" data-testid="ville-cta-phone" className="inline-flex items-center gap-2 rounded-full border border-argia-ink/20 px-7 py-3.5 text-sm font-semibold text-argia-ink transition-colors duration-300 hover:bg-argia-ink hover:text-white">
      06 33 83 74 56
    </a>
    <span className="text-sm text-argia-muted">Premier rendez-vous offert · {ville.name} ou à distance</span>
  </div>
);

export default function Ville({ ville }) {
  const others = villes.filter((v) => v.slug !== ville.slug);

  return (
    <div data-testid={`ville-page-${ville.slug}`}>
      <section className="relative overflow-hidden grain">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-16 lg:px-10 lg:pt-24">
          <Reveal>
            <SectionLabel>{ville.region}</SectionLabel>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-argia-ink sm:text-5xl lg:text-6xl" data-testid="ville-title">
              {ville.h1[0]} <span className="whitespace-nowrap italic text-argia-red">{ville.h1[1]}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-argia-muted md:text-lg">{ville.intro}</p>
            <div className="mt-6 flex flex-wrap gap-5 text-sm text-argia-muted">
              <span className="inline-flex items-center gap-2"><MapPin size={16} strokeWidth={1.5} className="text-argia-red" />{ville.name} ({ville.dept})</span>
              <span className="inline-flex items-center gap-2"><Car size={16} strokeWidth={1.5} className="text-argia-red" />{ville.distance}</span>
            </div>
            <div className="mt-10"><Cta ville={ville} /></div>
          </Reveal>
        </div>
      </section>

      <section className="bg-argia-stone py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <SectionLabel>Proximité</SectionLabel>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-argia-ink sm:text-4xl">Une gestionnaire administrative proche de {ville.name}</h2>
            <p className="mt-5 text-base leading-relaxed text-argia-muted">{ville.context}</p>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="ville-nearby">
              {ville.nearby.map((n) => (
                <span key={n} className="rounded-full border border-argia-ink/15 bg-white px-3.5 py-1.5 text-xs font-medium text-argia-ink">{n}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-[#2C2A29]/10 bg-white p-8">
              <h3 className="font-display text-xl font-bold text-argia-ink">Pour qui, à {ville.name} ?</h3>
              <ul className="mt-5 space-y-3">
                {ville.publics.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-argia-muted">
                    <CheckCircle2 size={18} strokeWidth={1.5} className="mt-0.5 shrink-0 text-argia-red" />{p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <Reveal>
          <SectionLabel>Besoins fréquents</SectionLabel>
          <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight text-argia-ink sm:text-4xl">Ce que je règle pour les professionnels et particuliers de {ville.name}</h2>
        </Reveal>
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {ville.besoins.map((b, i) => (
            <Reveal key={b} delay={i * 0.08}>
              <div className="flex gap-4 rounded-2xl border border-[#2C2A29]/10 bg-white p-6">
                <span className="font-display text-2xl font-bold text-argia-gold">0{i + 1}</span>
                <p className="text-sm leading-relaxed text-argia-ink md:text-base">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-argia-ink py-20" data-testid="ville-services">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-argia-gold">Prestations</span>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">Mes services de gestion administrative à {ville.name}</h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-white/10 p-7">
                  <s.icon size={24} strokeWidth={1.5} className="text-argia-gold" />
                  <h3 className="mt-4 font-display text-lg font-bold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/70">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link to="/services" className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-argia-gold hover:text-white" data-testid="ville-link-services">
              Voir le détail des services <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-20">
        <Reveal>
          <SectionLabel>Questions fréquentes</SectionLabel>
          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-argia-ink sm:text-4xl">Gestion administrative à {ville.name} : vos questions</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-10 space-y-4" data-testid="ville-faq">
            {ville.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`v-${i}`} className="rounded-2xl border border-[#2C2A29]/10 bg-white px-6 data-[state=open]:shadow-md">
                <AccordionTrigger data-testid={`ville-faq-${i}`} className="py-5 text-left font-display text-lg font-bold text-argia-ink hover:text-argia-red hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="pb-5 text-base leading-relaxed text-argia-muted">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <Reveal>
          <div className="rounded-2xl bg-argia-stone p-10">
            <h2 className="font-display text-2xl font-bold text-argia-ink md:text-3xl">Allégeons votre administratif à {ville.name}</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-argia-muted md:text-base">Quelques questions rapides, un créneau synchronisé avec mon agenda, et nous faisons le point — sur place ou en visio.</p>
            <div className="mt-7"><Cta ville={ville} /></div>
            <div className="mt-10 border-t border-argia-ink/10 pt-6">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-argia-muted">J'interviens aussi à</p>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2" data-testid="ville-others">
                {others.map((o) => (
                  <Link key={o.slug} to={o.path} data-testid={`ville-link-${o.slug}`} className="text-sm font-medium text-argia-ink transition-colors hover:text-argia-red">{o.name}</Link>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
